import AdminHeader from "components/Headers/AdminHeader";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Badge,
  Container,
  Row,
  Col,
  Card,
  Input,
  CardHeader,
  CardBody,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";

import CommonTable from "components/CommonComponents/ReactTable/Table";
import {
  fixedWidthCustomStyle,
  DEFAULT_RECORDS_PER_PAGE,
} from "constants/CommonConstants";
import {
  BATCH_STATUS_OPTIONS,
  COURSES,
  batchData,
  instructorsList,
} from "components/constant";
import { ISOToHumanReadableDate } from "components/CommonComponents/CommonFunctions";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";

const INSTRUCTOR_OPTIONS = [
  ...new Map(
    instructorsList
      .filter((inst) => inst.status === "ACTIVE")
      .map((inst) => [
        inst.fullName,
        {
          label: inst.fullName,
          value: inst.fullName,
        },
      ]),
  ).values(),
];

const getStatusColor = (status) => {
  switch (status) {
    case "Upcoming":
      return "primary";
    case "Ongoing":
      return "warning";
    case "Completed":
      return "success";
    case "Cancelled":
      return "danger";
    default:
      return "secondary";
  }
};

function BatchListing() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    batchId: "",
    sessionName: "",
    courseName: null,
    Instructor: null,
    searchStatus: null,
    date: "",
    filterParams: [],
  });

  const handleSearch = () => {};

  const handleClear = () => {
    setFilterState({
      batchId: "",
      sessionName: "",
      courseName: null,
      Instructor: null,
      searchStatus: null,
      date: "",
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge color={getStatusColor(props.data.label)} pill>
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge color={getStatusColor(props.data.label)} pill>
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    {
      Header: "Batch ID",
      accessor: "batchId",
    },
    {
      Header: "Course Name",
      accessor: "courseName",
    },
    {
      Header: "Session Name",
      accessor: "sessionName",
    },
    {
      Header: "Date",
      Cell: ({ row }) => {
        const { startDate, endDate } = row.original;
        if (!startDate || !endDate) return "-";
        return (
          <div>
            <div>{ISOToHumanReadableDate(startDate)}</div>
            <div>{ISOToHumanReadableDate(endDate)}</div>
          </div>
        );
      },
    },
    {
      Header: "Duration",
      Cell: ({ row }) => {
        const { startDate, endDate } = row.original;
        if (!startDate || !endDate) return "-";
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffInDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        return `${diffInDays} days`;
      },
    },
    {
      Header: "Instructors",
      accessor: "instructors",
      Cell: ({ value }) =>
        value?.length ? (
          <ul className="mb-0 pl-3">
            {value.map((inst, index) => (
              <li key={index}>{inst}</li>
            ))}
          </ul>
        ) : (
          "-"
        ),
    },
    {
      Header: "Enrolled",
      accessor: "enrolledCount",
    },
    {
      Header: "Available Seats",
      accessor: "availableSeats",
    },
    {
      Header: "Offline Training Center",
      accessor: "offlineTrainingCenter",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Min Participants",
      accessor: "minParticipant",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Max Participants",
      accessor: "maxParticipant",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge color={getStatusColor(value)} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const id = row.original.id;
        const encodedId = urlEncodedId(row.original.id);
        const isActive = row.original.status === "ACTIVE";

        return (
          <div className="d-flex gap-2">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`edit${encodedId}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/batch/edit/${encodedId}`
                    : `/admin/batch/edit/${encodedId}`,
                );
              }}
            >
              <i className="fas fa-edit mr-1 table-action-info" />
              {/* Edit */}
            </Button>
            <UncontrolledTooltip target={`edit${encodedId}`}>
              Edit
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <AdminHeader />
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card>
              {/* head */}
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Batches</h3>
                <Button
                  className="btn-deep-blue"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? "/super-admin/batch/add-batch"
                        : "/admin/batch/add-batch",
                      {
                        state: { mode: "add" },
                      },
                    )
                  }
                >
                  <i className="fa fa-plus mr-1" />
                  Batch
                </Button>
              </CardHeader>

              {/* body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by batch id"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.batchId}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          batchId: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={COURSES}
                      placeholder="Course Name"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={filterState.courseName}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          courseName: selected,
                        }))
                      }
                    />

                    <Input
                      type="text"
                      placeholder="Search by session name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.sessionName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          sessionName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      //   isMulti
                      className="mr-2 mb-2 filter-select"
                      options={INSTRUCTOR_OPTIONS}
                      placeholder="Instructor"
                      value={filterState.Instructor}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          Instructor: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                    />
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={BATCH_STATUS_OPTIONS}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                      // isClearable
                    />

                    <div className="d-flex mr-2 mb-2 align-items-center date-range-wrapper">
                      <span className="mr-2">From</span>
                      <Input type="date" className="mr-1 mb-1 filter-select" />
                      <span className="mx-1">To</span>
                      <Input type="date" className="ml-1 mb-1 filter-select" />
                    </div>

                    <Button
                      color="default"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                      onClick={handleSearch}
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>

                    {Object.keys(filterState.filterParams).length > 0 && (
                      <Button
                        color="default"
                        className="ml-2 mb-0 filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {batchData.length ? (
                    <span className="total-records">
                      Total Records : {batchData.length}
                    </span>
                  ) : null}
                </div>

                <CommonTable
                  columns={columns}
                  data={batchData.slice(
                    (pageNum - 1) * (DEFAULT_RECORDS_PER_PAGE || 10),
                    pageNum * (DEFAULT_RECORDS_PER_PAGE || 10),
                  )}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 10}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                  totalPages={Math.ceil(
                    batchData.length / DEFAULT_RECORDS_PER_PAGE,
                  )}
                  showPaginationBottom={true}
                  //   rowClick={handleOnRowClick}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default BatchListing;
